import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, ShieldAlert, AlertCircle, Mail, Eye, EyeOff } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminLogin() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const [authConfig, setAuthConfig] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate('/admin');
  }, [user, navigate]);

  useEffect(() => {
    authApi.getConfig().then(res => setAuthConfig(res.data)).catch(() => setAuthConfig({ provider: 'emergent' }));
  }, []);

  const handleGoogleLogin = () => {
    if (!authConfig) return;

    if (authConfig.provider === 'google' && authConfig.google_client_id) {
      // Direct Google OAuth — production mode (Gandi)
      const redirectUri = `${window.location.origin}/api/auth/google/callback`;
      const params = new URLSearchParams({
        client_id: authConfig.google_client_id,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'offline',
        prompt: 'select_account',
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    } else {
      // Emergent Auth — development/preview mode
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + '/admin';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.login({ email: email.trim().toLowerCase(), password });
      if (checkAuth) await checkAuth();
      toast.success('Connexion réussie');
      navigate('/admin');
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) toast.error('Identifiants invalides');
      else if (status === 403) toast.error('Compte désactivé');
      else toast.error(err.response?.data?.detail || 'Erreur de connexion');
    }
    setSubmitting(false);
  };

  const googleEnabled = authConfig?.provider === 'google' && authConfig?.google_client_id;
  const emergentEnabled = authConfig?.provider === 'emergent';

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-10" data-testid="admin-login-page">
      <div className="bg-white rounded-3xl p-10 shadow-lg border border-garden-800/5 max-w-md w-full">
        <div className="text-center">
          <span className="font-accent text-3xl text-garden-800 block mb-2">Côté Terrasse</span>
          <h1 className="text-2xl font-heading font-bold text-garden-800 mb-2">Administration</h1>
          <p className="text-sm text-charcoal/60 mb-6">
            Connectez-vous pour gérer le contenu de votre site.
          </p>
        </div>

        {error === 'forbidden' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="login-error-forbidden">
            <ShieldAlert size={20} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 text-left">
              Votre adresse email n'est pas autorisée à accéder à l'administration. Contactez le gérant du restaurant.
            </p>
          </div>
        )}
        {(error === 'auth' || error === 'config') && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="login-error-auth">
            <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 text-left">
              Erreur d'authentification. Veuillez réessayer.
            </p>
          </div>
        )}

        {/* Email + password form */}
        <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="password-login-form">
          <div>
            <Label className="text-sm">Email</Label>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              className="border-garden-200 rounded-xl mt-1"
              data-testid="login-email"
            />
          </div>
          <div>
            <Label className="text-sm">Mot de passe</Label>
            <div className="relative">
              <Input
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                className="border-garden-200 rounded-xl mt-1 pr-10"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal/70"
                aria-label={showPwd ? 'Masquer' : 'Afficher'}
                data-testid="login-password-toggle"
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="password-login-submit"
            className="bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-base font-medium w-full"
          >
            <Mail size={18} className="mr-2" />
            {submitting ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>

        {/* Separator + Google/Emergent button */}
        {(googleEnabled || emergentEnabled) && (
          <>
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-garden-100" />
              <span className="text-xs text-charcoal/40 uppercase tracking-wide">ou</span>
              <div className="flex-1 h-px bg-garden-100" />
            </div>

            <Button
              onClick={handleGoogleLogin}
              disabled={!authConfig}
              variant="outline"
              data-testid="admin-login-btn"
              className="border-garden-200 hover:bg-garden-50 text-garden-800 rounded-full py-6 text-base font-medium w-full"
            >
              <LogIn size={18} className="mr-2" />
              {googleEnabled ? 'Se connecter avec Google' : 'Se connecter avec Emergent'}
            </Button>
          </>
        )}

        <Link to="/" className="text-sm text-garden-600 hover:text-garden-800 mt-6 block text-center">
          Retour au site
        </Link>
      </div>
    </div>
  );
}
